/** Range Planning "Attributes & Allergens" detail page (Demo Mode only). Tabbed layout
 * follows the same pattern as RangePlanningItems/ItemDetail.tsx and MenuDetail.tsx. */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, CircularProgress, IconButton, Stack, SvgIcon, Tab, Tabs, Typography } from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { ArrowLeft } from '../../assets';
import useAttributeDetail from './hooks/useAttributeDetail';
import useConflicts from './hooks/useConflicts';
import { AttributeDetailTab, AttributeGovernanceStatus } from './types';
import OverviewTab from './components/OverviewTab';
import UsageTab from './components/UsageTab';
import ConflictsTab from './components/ConflictsTab';

const statusColor: Record<AttributeGovernanceStatus, 'success' | 'warning' | 'default'> = {
  [AttributeGovernanceStatus.Active]: 'success',
  [AttributeGovernanceStatus.UnderReview]: 'warning',
  [AttributeGovernanceStatus.Deprecated]: 'default',
};

const AttributeDetail = () => {
  const { t } = useTranslation('attributesAllergens');
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const { attribute, usage, isLoading, loadAttribute } = useAttributeDetail();
  const { conflicts, loadConflicts } = useConflicts();
  const [tabValue, setTabValue] = useState<AttributeDetailTab>(AttributeDetailTab.Overview);

  useEffect(() => {
    if (code) loadAttribute(code);
  }, [code, loadAttribute]);

  useEffect(() => {
    loadConflicts();
  }, [loadConflicts]);

  if (isLoading || !attribute) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  const attributeConflicts = conflicts.filter(
    (c) => c.attributeCode === attribute.code || c.conflictingAttributeCode === attribute.code,
  );

  return (
    <PageArea>
      <Stack direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/range-planning/attributes-allergens')}
          aria-label={t('backToRegistry', 'Back to registry')}>
          <SvgIcon><ArrowLeft /></SvgIcon>
        </IconButton>
        <PageTitle>{attribute.name}</PageTitle>
        <Chip label={t(`governance.${attribute.governanceStatus}`, attribute.governanceStatus)}
          color={statusColor[attribute.governanceStatus]}
          size="small" />
      </Stack>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mb: 4 }}>
        {attribute.code} - {t(`category.${attribute.category}`, attribute.category)}
      </Typography>

      <Section>
        <Tabs value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tab label={t('tabs.overview', 'Overview')}
            value={AttributeDetailTab.Overview} />
          <Tab label={t('tabs.usage', 'Usage')}
            value={AttributeDetailTab.Usage} />
          <Tab label={t('tabs.conflicts', 'Conflicts')}
            value={AttributeDetailTab.Conflicts} />
        </Tabs>

        {tabValue === AttributeDetailTab.Overview && <OverviewTab attribute={attribute}
          usageCount={usage.length} />}
        {tabValue === AttributeDetailTab.Usage && <UsageTab usage={usage} />}
        {tabValue === AttributeDetailTab.Conflicts && <ConflictsTab conflicts={attributeConflicts} />}
      </Section>
    </PageArea>
  );
};

export default AttributeDetail;
